import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";

/* ── colour constants (matching site palette) ── */
const C = {
  saffron:"#E8922A", saffronL:"#FDF0D5", saffronD:"#B86A10",
  sage:"#4A7C59",    sageL:"#EAF4EE",    sageD:"#2D5A3D",
  terra:"#B85C38",
  cream:"#FDFAF3",   creamD:"#F5EDD8",
  turmeric:"#F7C948",
  ink:"#2C1810",     inkMid:"#5C3D2E",   inkLight:"#9A7B60",
};

/* ─── tiny reusable inputs (defined outside to avoid focus loss) ─── */
function Label({ text, hint }) {
  return (
    <div className="mb-1.5">
      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">{text}</label>
      {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function TInput({ value, onChange, placeholder }) {
  return (
    <input
      type="text" value={value} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-amber-300 focus:border-amber-400 bg-white"
    />
  );
}

function TArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value} placeholder={placeholder} rows={rows}
      onChange={e => onChange(e.target.value)}
      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm outline-none transition focus:ring-2 focus:ring-amber-300 focus:border-amber-400 resize-y bg-white"
    />
  );
}

/* ─── Live preview card (exact scaled replica of the Home.js card) ─── */
function LivePreview({ form, imgPreview }) {
  const dos    = (form.srDos    || "").split(/[|\n]/).map(s => s.trim()).filter(Boolean);
  const avoids = (form.srAvoids || "").split(/[|\n]/).map(s => s.trim()).filter(Boolean);

  const imgSrc = imgPreview || form.imageUrl ||
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=70";

  const miniCards = [
    { icon: form.srCard1Icon || "🌅", title: form.srCard1Title || "Best Wake Time",  desc: form.srCard1Desc || "5:30 – 6:00 AM"    },
    { icon: form.srCard2Icon || "🌾", title: form.srCard2Title || "Ideal Foods",     desc: form.srCard2Desc || "Barley, mung, honey" },
    { icon: form.srCard3Icon || "🧘", title: form.srCard3Title || "Mind Practice",   desc: form.srCard3Desc || "Meditation 10 min"  },
    { icon: form.srCard4Icon || "🌿", title: form.srCard4Title || "Key Herb",        desc: form.srCard4Desc || "Triphala at bedtime" },
  ];

  return (
    <div style={{
      background: "linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%)",
      borderRadius: 20, overflow: "hidden", position: "relative",
      boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
    }}>
      {/* Subtle bg decorations */}
      <div style={{ position:"absolute", right:-40, top:-40, opacity:0.05, fontSize:180, pointerEvents:"none", lineHeight:1 }}>✿</div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(280px,100%),1fr))" }}>

        {/* ── LEFT: Photo ── */}
        <div style={{ position:"relative", minHeight:300, overflow:"hidden" }}>
          <img
            src={imgSrc} alt="Season"
            style={{
              width:"100%", height:"100%", minHeight:300,
              objectFit:"cover", display:"block",
              filter:"brightness(0.7) saturate(1.15)",
            }}
          />
          {/* overlays */}
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to right,rgba(10,8,20,0.55) 0%,rgba(10,8,20,0.1) 60%,transparent 100%)", pointerEvents:"none" }}/>
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(15,52,96,0.7) 0%,transparent 50%)", pointerEvents:"none" }}/>

          {/* season badge */}
          <div style={{
            position:"absolute", top:18, left:18,
            display:"inline-flex", alignItems:"center", gap:6,
            background:"rgba(247,201,72,0.18)", backdropFilter:"blur(10px)",
            borderRadius:20, padding:"5px 14px",
            border:"1px solid rgba(247,201,72,0.45)",
          }}>
            <span style={{ fontSize:14 }}>{form.seasonEmoji || "🌿"}</span>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.12em",
              color:C.turmeric, textTransform:"uppercase", fontFamily:"sans-serif" }}>
              {form.seasonLabel || "Current Season"}
            </span>
          </div>

          {/* season name at bottom */}
          <div style={{ position:"absolute", bottom:18, left:18 }}>
            <p style={{ margin:0, fontSize:22, fontFamily:"Georgia,serif", fontWeight:800,
              color:"#fff", lineHeight:1.15, textShadow:"0 2px 10px rgba(0,0,0,0.5)" }}>
              {form.seasonName || "Vasanta Ṛtu"}
            </p>
            <p style={{ margin:"4px 0 0", fontSize:12, color:"rgba(255,255,255,0.65)",
              fontFamily:"Georgia,serif", fontStyle:"italic" }}>
              {form.seasonPeriod || "Spring · Mar – May"}
            </p>
          </div>
        </div>

        {/* ── RIGHT: Content ── */}
        <div style={{ padding:"clamp(20px,4vw,40px) clamp(18px,4vw,36px)", display:"flex", flexDirection:"column", gap:14 }}>

          {/* badge */}
          <div style={{
            display:"inline-flex", alignItems:"center", gap:7, alignSelf:"flex-start",
            background:"rgba(255,255,255,0.08)", borderRadius:20, padding:"4px 14px",
            border:"1px solid rgba(255,255,255,0.15)",
          }}>
            <span style={{ fontSize:13 }}>🍃</span>
            <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.13em",
              color:"rgba(255,255,255,0.7)", textTransform:"uppercase", fontFamily:"sans-serif" }}>
              {form.srBadge || "Seasonal Wisdom"}
            </span>
          </div>

          <div>
            <h2 style={{ fontFamily:"Georgia,serif", fontSize:24, fontWeight:800,
              color:"#fff", lineHeight:1.2, margin:"0 0 5px" }}>
              {form.srHeading1 || "Seasonal Regimen"}
            </h2>
            <p style={{ fontSize:13, color:"rgba(255,255,255,0.5)", fontFamily:"Georgia,serif",
              fontStyle:"italic", margin:0, lineHeight:1.6 }}>
              {form.srSubtitle || "Simple Ayurvedic guidance — live in harmony with nature's rhythms."}
            </p>
          </div>

          {/* Do's & Avoid grid */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {/* Do's */}
            <div style={{ background:"rgba(74,124,89,0.18)", borderRadius:14, padding:"14px 16px",
              border:"1px solid rgba(74,124,89,0.35)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                <span style={{ fontSize:16 }}>✅</span>
                <span style={{ fontSize:11, fontWeight:800, color:"rgba(144,238,144,0.9)",
                  textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:"sans-serif" }}>Do's</span>
              </div>
              {(dos.length ? dos : ["—"]).map((item, i) => (
                <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start", marginBottom:6 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", flexShrink:0,
                    background:"rgba(144,238,144,0.7)", marginTop:5 }}/>
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.78)",
                    fontFamily:"sans-serif", lineHeight:1.5 }}>{item}</span>
                </div>
              ))}
            </div>

            {/* Avoid */}
            <div style={{ background:"rgba(184,92,56,0.15)", borderRadius:14, padding:"14px 16px",
              border:"1px solid rgba(184,92,56,0.32)" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
                <span style={{ fontSize:16 }}>🚫</span>
                <span style={{ fontSize:11, fontWeight:800, color:"rgba(255,160,122,0.9)",
                  textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:"sans-serif" }}>Avoid</span>
              </div>
              {(avoids.length ? avoids : ["—"]).map((item, i) => (
                <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start", marginBottom:6 }}>
                  <div style={{ width:5, height:5, borderRadius:"50%", flexShrink:0,
                    background:"rgba(255,160,122,0.65)", marginTop:5 }}/>
                  <span style={{ fontSize:12, color:"rgba(255,255,255,0.78)",
                    fontFamily:"sans-serif", lineHeight:1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Vaidya tip */}
          <div style={{ background:"rgba(247,201,72,0.1)", borderRadius:12, padding:"12px 16px",
            border:"1px solid rgba(247,201,72,0.22)", display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:20, flexShrink:0, marginTop:1 }}>💡</span>
            <div>
              <p style={{ margin:"0 0 2px", fontSize:10, fontWeight:700, color:C.turmeric,
                textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:"sans-serif" }}>Vaidya's Tip</p>
              <p style={{ margin:0, fontSize:12, color:"rgba(255,255,255,0.7)",
                fontFamily:"Georgia,serif", fontStyle:"italic", lineHeight:1.6 }}>
                {form.srVaidyaTip || "A seasonal tip from your doctor will appear here."}
              </p>
            </div>
          </div>

          {/* Mini cards */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {miniCards.map((card, i) => (
              <div key={i} style={{ background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.1)", borderRadius:12,
                padding:"10px 13px", display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ fontSize:20, flexShrink:0 }}>{card.icon}</span>
                <div>
                  <p style={{ margin:0, fontSize:9, fontWeight:700,
                    color:"rgba(255,255,255,0.45)", fontFamily:"sans-serif",
                    textTransform:"uppercase", letterSpacing:"0.08em" }}>{card.title}</p>
                  <p style={{ margin:0, fontSize:12, color:"rgba(255,255,255,0.88)",
                    fontFamily:"sans-serif", fontWeight:600 }}>{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Default form state matching SeasonalRegimen model defaults ── */
const DEFAULT_FORM = {
  seasonName:   "Vasanta Ṛtu",
  seasonPeriod: "Spring · Mar – May",
  seasonEmoji:  "🌿",
  seasonLabel:  "Current Season",
  imageUrl:     "",
  srBadge:      "Seasonal Wisdom",
  srHeading1:   "Seasonal Regimen",
  srSubtitle:   "Simple Ayurvedic guidance for the general public — live in harmony with nature's rhythms.",
  srDos:        "Eat light warm meals|Wake at sunrise|Use bitter herbs (neem, turmeric)|Light yoga & pranayama daily|Drink warm ginger water",
  srAvoids:     "Heavy oily & fermented foods|Sleeping during the day|Cold drinks & ice-cream|Excess sour & salty intake|Skipping meals or harsh fasting",
  srVaidyaTip:  "This season, focus on clearing accumulated Kapha. Start your day with a cup of warm honey-ginger water and a 15-minute walk outdoors.",
  srCard1Icon: "🌅", srCard1Title: "Best Wake Time",  srCard1Desc: "5:30 – 6:00 AM",
  srCard2Icon: "🌾", srCard2Title: "Ideal Foods",     srCard2Desc: "Barley, mung, honey",
  srCard3Icon: "🧘", srCard3Title: "Mind Practice",   srCard3Desc: "Meditation 10 min",
  srCard4Icon: "🌿", srCard4Title: "Key Herb",        srCard4Desc: "Triphala at bedtime",
  isPublished: true,
};

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
export default function AdminSeasonalRegimen() {
  const [form,       setForm]       = useState(DEFAULT_FORM);
  const [imgFile,    setImgFile]    = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [removeImg,  setRemoveImg]  = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState({ text:"", ok:true });
  const [dirty,      setDirty]      = useState(false);
  const fileRef = useRef();

  /* ── Load existing regimen ── */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/seasonal-regimen");
      const d = r.data || {};
      setForm({ ...DEFAULT_FORM, ...d });
      setImgPreview(d.imageUrl || "");
    } catch {
      /* use defaults */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Field updater ── */
  const setF = (key, val) => {
    setDirty(true);
    setForm(p => ({ ...p, [key]: val }));
  };

  /* ── Image handling ── */
  const handleFile = e => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setRemoveImg(false);
    setDirty(true);
    const reader = new FileReader();
    reader.onloadend = () => setImgPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearImg = () => {
    setImgFile(null);
    setImgPreview("");
    setRemoveImg(true);
    setDirty(true);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ── Save ── */
  const save = async () => {
    setSaving(true);
    setMsg({ text:"", ok:true });
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== "imageUrl" && k !== "_id" && k !== "__v" && k !== "createdAt" && k !== "updatedAt") {
          fd.append(k, v === true ? "true" : v === false ? "false" : v ?? "");
        }
      });
      if (imgFile)     fd.append("image", imgFile);
      if (removeImg)   fd.append("removeImage", "true");

      await api.put("/seasonal-regimen", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg({ text: "✅ Seasonal Regimen updated and live on the website!", ok: true });
      setDirty(false);
      setImgFile(null);
      setRemoveImg(false);
      load();
    } catch (err) {
      setMsg({ text: "❌ " + (err.response?.data?.error || "Save failed. Please try again."), ok: false });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Field group config ─── */
  const fieldGroups = [
    {
      title: "📸 Season Photo & Identity",
      hint: "The full-bleed photo on the left side of the card.",
      fields: [
        { key:"seasonEmoji",  label:"Season Emoji",       hint:'e.g. 🌸 spring  🌞 summer  🍂 autumn  ❄️ winter', type:"text" },
        { key:"seasonLabel",  label:"Badge Label",        hint:'Short label on the badge e.g. "Current Season"',  type:"text" },
        { key:"seasonName",   label:"Season Name",        hint:'Shown large on the photo e.g. "Vasanta Ṛtu"',      type:"text" },
        { key:"seasonPeriod", label:"Season Period",      hint:'e.g. "Spring · Mar – May"',                        type:"text" },
      ],
    },
    {
      title: "📝 Card Headline & Subtitle",
      fields: [
        { key:"srBadge",    label:"Top Badge Text",  hint:'e.g. "Seasonal Wisdom"',                   type:"text" },
        { key:"srHeading1", label:"Card Headline",   hint:'e.g. "Seasonal Regimen"',                  type:"text" },
        { key:"srSubtitle", label:"Subtitle",        hint:"One-liner shown below the headline",        type:"textarea" },
      ],
    },
    {
      title: "✅ Do's",
      hint: 'Press Enter for a new line, or use a pipe  |  — either works. e.g.  "Wake early\\nEat warm meals\\nDrink ginger water"',
      fields: [
        { key:"srDos", label:"Do's List", hint:"One point per line (press Enter), or separate with  |", type:"textarea", rows:4 },
      ],
    },
    {
      title: "🚫 Avoid",
      hint: 'Press Enter for a new line, or use a pipe  |  — either works. e.g.  "Heavy oily foods\\nDay sleep\\nCold drinks"',
      fields: [
        { key:"srAvoids", label:"Avoid List", hint:"One point per line (press Enter), or separate with  |", type:"textarea", rows:4 },
      ],
    },
    {
      title: "💡 Vaidya's Tip",
      hint: "Shown in the amber highlighted box — one clear, actionable seasonal tip.",
      fields: [
        { key:"srVaidyaTip", label:"Tip Text", hint:"e.g. Start your day with warm ginger water and a 15-minute walk outdoors.", type:"textarea", rows:3 },
      ],
    },
    {
      title: "🗃️ Mini Info Cards (4 quick facts)",
      fields: [
        { key:"srCard1Icon",  label:"Card 1 — Icon",  type:"text", inline:true },
        { key:"srCard1Title", label:"Card 1 — Label", type:"text", inline:true },
        { key:"srCard1Desc",  label:"Card 1 — Value", type:"text", inline:true },
        { key:"srCard2Icon",  label:"Card 2 — Icon",  type:"text", inline:true },
        { key:"srCard2Title", label:"Card 2 — Label", type:"text", inline:true },
        { key:"srCard2Desc",  label:"Card 2 — Value", type:"text", inline:true },
        { key:"srCard3Icon",  label:"Card 3 — Icon",  type:"text", inline:true },
        { key:"srCard3Title", label:"Card 3 — Label", type:"text", inline:true },
        { key:"srCard3Desc",  label:"Card 3 — Value", type:"text", inline:true },
        { key:"srCard4Icon",  label:"Card 4 — Icon",  type:"text", inline:true },
        { key:"srCard4Title", label:"Card 4 — Label", type:"text", inline:true },
        { key:"srCard4Desc",  label:"Card 4 — Value", type:"text", inline:true },
      ],
    },
  ];

  /* ─── Render inline mini-card fields in groups of 3 ─── */
  const renderMiniCardFields = (fields) => {
    const chunks = [];
    for (let i = 0; i < fields.length; i += 3) chunks.push(fields.slice(i, i + 3));
    return chunks.map((chunk, ci) => (
      <div key={ci} className="grid grid-cols-3 gap-3 mb-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
        {chunk.map(f => (
          <div key={f.key}>
            <Label text={f.label.replace(/^Card \d — /, "")} />
            <TInput value={form[f.key] || ""} onChange={v => setF(f.key, v)} placeholder="" />
          </div>
        ))}
      </div>
    ));
  };

  return (
    <div className="space-y-6 max-w-7xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">🍃 Seasonal Regimen</h1>
        <p className="text-sm text-gray-400 mt-1">
          Edit the seasonal health card shown below Daily Insights on the home page. The preview updates live as you type.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-20 text-gray-400 text-sm">Loading…</div>
      ) : (
        <div className="grid xl:grid-cols-2 gap-8 items-start">

          {/* ════ LEFT: EDITOR ════ */}
          <div className="space-y-5">

            {/* Msg */}
            {msg.text && (
              <div className={`text-sm font-medium px-4 py-3 rounded-xl ${msg.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
                {msg.text}
              </div>
            )}

            {/* Image upload */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-3">
              <h2 className="text-sm font-bold text-gray-700 pb-2 border-b border-gray-100">
                📸 Season Photo
              </h2>
              <p className="text-xs text-gray-400">
                Upload a high-quality photo for the current season (mountains, nature, herbs, etc.)
              </p>

              {imgPreview && !removeImg ? (
                <div className="relative inline-block group">
                  <img src={imgPreview} alt="Season preview"
                    className="h-44 w-full object-cover rounded-xl border border-gray-200 shadow-sm" />
                  <button onClick={clearImg}
                    className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 text-white rounded-full text-sm shadow flex items-center justify-center hover:bg-red-600 transition">
                    ✕
                  </button>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-xl transition flex items-center justify-center">
                    <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transition">🔍</span>
                  </div>
                </div>
              ) : (
                <div className="h-32 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-2 text-gray-400 bg-gray-50">
                  <span className="text-3xl">🏔️</span>
                  <span className="text-xs">No photo uploaded yet</span>
                </div>
              )}

              <input
                ref={fileRef} type="file" accept="image/*" onChange={handleFile}
                className="block text-sm text-gray-500
                  file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0
                  file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700
                  hover:file:bg-amber-100 transition cursor-pointer"
              />
              <p className="text-xs text-gray-400">Max 5 MB · JPG, PNG or WebP · Recommended: landscape, 1200×800px+</p>
            </div>

            {/* Field groups */}
            {fieldGroups.map((group, gi) => (
              <div key={gi} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
                <div className="pb-2 border-b border-gray-100">
                  <h2 className="text-sm font-bold text-gray-700">{group.title}</h2>
                  {group.hint && <p className="text-xs text-gray-400 mt-0.5">{group.hint}</p>}
                </div>

                {group.fields[0]?.inline
                  ? renderMiniCardFields(group.fields)
                  : group.fields.map(f => (
                    <div key={f.key}>
                      <Label text={f.label} hint={f.hint} />
                      {f.type === "textarea"
                        ? <TArea value={form[f.key] || ""} onChange={v => setF(f.key, v)} placeholder={`Enter ${f.label.toLowerCase()}…`} rows={f.rows || 3} />
                        : <TInput value={form[f.key] || ""} onChange={v => setF(f.key, v)} placeholder={`Enter ${f.label.toLowerCase()}…`} />
                      }
                    </div>
                  ))
                }
              </div>
            ))}

            {/* Publish toggle */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <button onClick={() => setF("isPublished", !form.isPublished)}
                className="flex items-center gap-3 group">
                <div className={`relative w-11 h-6 rounded-full transition-colors ${form.isPublished ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isPublished ? "translate-x-5" : ""}`} />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-700 block">
                    {form.isPublished ? "Published — visible on website" : "Draft — hidden from visitors"}
                  </span>
                  <span className="text-xs text-gray-400">Toggle to show or hide the card from the home page</span>
                </div>
              </button>
            </div>

            {/* Save */}
            <div className="sticky bottom-0 pb-4 pt-2 bg-gray-50 z-10">
              <button
                onClick={save} disabled={saving || !dirty}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition shadow-sm"
                style={{
                  background: (saving || !dirty) ? "#d1d5db" : `linear-gradient(135deg,${C.sage},${C.sageD})`,
                  boxShadow: (!saving && dirty) ? `0 4px 18px rgba(74,124,89,0.38)` : "none",
                  cursor: (saving || !dirty) ? "not-allowed" : "pointer",
                }}>
                {saving ? "Saving…" : dirty ? "💾 Save & Go Live" : "✅ All Changes Saved"}
              </button>
              {dirty && (
                <p className="text-center text-xs text-amber-600 mt-2 font-medium">
                  ● Unsaved changes — preview reflects edits live
                </p>
              )}
            </div>
          </div>

          {/* ════ RIGHT: LIVE PREVIEW ════ */}
          <div className="xl:sticky xl:top-6">
            {/* Browser chrome */}
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
              {/* Chrome bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-gray-900">
                <div className="flex gap-1.5">
                  {["#ff5f57","#febc2e","#28c840"].map(c => (
                    <div key={c} style={{ width:10, height:10, borderRadius:"50%", background:c }}/>
                  ))}
                </div>
                <div className="flex-1 mx-3 bg-gray-700 rounded-md px-3 py-1.5 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 shrink-0"/>
                  <span className="text-xs text-gray-400 font-mono">yourclinic.com/#seasonal-regimen</span>
                </div>
                <div className="flex items-center gap-1.5 bg-green-900/50 border border-green-700/50 rounded-full px-2.5 py-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow:"0 0 5px #4ade80" }}/>
                  <span className="text-xs text-green-400 font-bold">LIVE</span>
                </div>
              </div>

              {/* Label bar */}
              <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center gap-2">
                <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Live Preview</span>
                <span className="text-xs text-amber-500">— every keystroke updates instantly</span>
              </div>

              {/* The actual preview */}
              <div className="p-4 bg-gray-100">
                <LivePreview form={form} imgPreview={imgPreview && !removeImg ? imgPreview : ""} />
              </div>

              {/* Footer */}
              <div className="bg-gray-900 px-4 py-2 text-center">
                <p className="text-xs text-gray-500">Save to push changes live on the website</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}